import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import AppHeader from "../components/AppHeader";
import { renderMarkdown } from "../lib/markdown";
import { Download, ArrowLeft, Loader2, Printer, AlertCircle } from "lucide-react";

const PHASES = [
  "Identificación del tipo de subasta",
  "Localización de la carga ejecutada",
  "Construcción del orden cronológico",
  "Análisis individual de cada carga",
  "Aplicación de reglas de cancelación",
  "Distinción de hipotecas y novaciones",
  "Comprobación de hipotecas vencidas",
  "Análisis de afecciones fiscales",
  "Prohibiciones de disponer",
  "Servidumbres",
  "Asientos pendientes de despacho",
  "Certificación de cargas",
  "Clasificación de riesgos",
  "Análisis de adjudicación",
  "Análisis de expediente",
  "Evaluación de ocupación",
];

const formatMoney = (n) => Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const formatDate = (iso) => {
  try { return new Date(iso).toLocaleString("es-ES"); } catch { return iso; }
};

export default function ReportView() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/reports/${id}`);
        if (cancelled) return;
        setReport(data);
        setLoading(false);
        if (data.status === "processing") {
          pollRef.current = setTimeout(fetchOnce, 4000);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOnce();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [id]);

  useEffect(() => {
    if (report?.status !== "processing") return;
    const t = setInterval(() => setPhase((p) => (p < PHASES.length - 1 ? p + 1 : p)), 7500);
    return () => clearInterval(t);
  }, [report?.status]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <AppHeader />
        <div className="p-16 flex items-center justify-center text-stone-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando informe…
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-stone-50">
        <AppHeader />
        <div className="p-16 text-center text-stone-500">Informe no encontrado</div>
      </div>
    );
  }

  if (report.status === "failed") {
    return (
      <div className="min-h-screen bg-stone-50">
        <AppHeader />
        <main className="max-w-2xl mx-auto px-6 py-16">
          <div className="bg-white border border-red-200 p-10 rounded-sm">
            <AlertCircle className="w-10 h-10 text-red-600 mb-4" />
            <h2 className="font-display text-2xl font-black mb-3">Error al generar el informe</h2>
            <p className="text-sm text-stone-600 mb-6">{report.error || "Se ha producido un error inesperado."}</p>
            <Link to="/generate" className="text-xs tracking-[0.18em] uppercase font-bold text-[#E0651F]">Intentar de nuevo →</Link>
          </div>
        </main>
      </div>
    );
  }

  if (report.status === "processing") {
    return (
      <div className="min-h-screen bg-stone-50 grain-bg">
        <AppHeader />
        <main className="max-w-4xl mx-auto px-6 py-12">
          <Link to="/" className="text-xs tracking-[0.18em] uppercase font-bold text-stone-500 hover:text-[#2d1f1a] inline-flex items-center gap-1.5 mb-6" data-testid="back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Volver al panel
          </Link>
          <div className="bg-[#2d1f1a] text-white p-10 rounded-sm">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-400 mb-3">Análisis en progreso</div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-none mb-3">
              Generando informe para {report.client_name}…
            </h1>
            <p className="text-stone-400 text-sm mb-8 max-w-xl">
              El sistema está aplicando el protocolo de 16 fases. Esto puede tardar entre 1 y 3 minutos. Puedes cerrar esta pestaña; el informe quedará guardado en tu histórico.
            </p>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5" data-testid="processing-phases">
              {PHASES.map((p, i) => {
                const isCurrent = i === phase;
                const isDone = i < phase;
                return (
                  <li key={i} className="flex items-start gap-3 text-xs leading-relaxed">
                    <span className={`font-mono w-6 ${isCurrent ? "text-blue-400" : isDone ? "text-emerald-400" : "text-stone-500"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={isCurrent ? "text-white font-medium" : isDone ? "text-stone-500 line-through" : "text-stone-300"}>
                      {p}
                      {isCurrent && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />}
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </main>
      </div>
    );
  }

  const html = renderMarkdown(report.report_markdown || "");

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="no-print"><AppHeader /></div>

      <div className="no-print max-w-4xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/" className="text-xs tracking-[0.18em] uppercase font-bold text-stone-500 hover:text-[#2d1f1a] inline-flex items-center gap-1.5" data-testid="back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Volver
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-white border border-stone-300 hover:border-[#2d1f1a] text-[#2d1f1a] px-4 py-2 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
            data-testid="print-btn"
          >
            <Printer className="w-3.5 h-3.5" /> Imprimir
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-4 py-2 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors"
            data-testid="download-pdf-btn"
          >
            <Download className="w-3.5 h-3.5" /> Descargar PDF
          </button>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <article className="print-area bg-white border border-stone-200 shadow-md p-8 md:p-14" data-testid="report-content">
          <header className="border-b-2 border-slate-950 pb-6 mb-8">
            <div className="flex items-start justify-between gap-6 mb-6">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-stone-500 mb-2">FM Subastas & Investment</div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-none text-[#2d1f1a]">
                  Informe de Deudas y Cargas
                </h1>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500">Emitido</div>
                <div className="text-xs font-mono text-stone-700 mt-1">{formatDate(report.created_at)}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-3 text-sm">
              <DataCell label="Cliente" value={report.client_name} />
              <DataCell label="ID Subasta" value={report.auction_id} mono />
              <DataCell label="Valor Subasta" value={formatMoney(report.auction_value)} mono />
              <DataCell label="Depósito" value={formatMoney(report.deposit_amount)} mono />
              <DataCell label="Tramos Pujas" value={report.bid_increment || "—"} />
            </div>
          </header>

          <div className="report-prose" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }) }} />

          <footer className="mt-16 pt-6 border-t border-stone-200 text-[10px] tracking-[0.25em] uppercase text-stone-400 flex justify-between">
            <span>FM Subastas & Investment</span>
            <span>Informe {report.id?.slice(0, 8)}</span>
          </footer>
        </article>
      </main>
    </div>
  );
}

function DataCell({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-1">{label}</div>
      <div className={`text-[#2d1f1a] font-medium ${mono ? "font-mono text-xs" : "text-sm"}`}>{value}</div>
    </div>
  );
}
